import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useCompany } from "../../context/CompanyContext";
import { supabase } from "../../Services/supabaseClient";
import {
  sendQuoteEmail,
  markQuoteEmailFailed,
} from "../services/crmQuoteEmailService";
import useCrmPermissions from "../hooks/useCrmPermissions";
import {
  convertQuoteToInvoice,
  getQuoteById,
  markQuoteAccepted,
  markQuoteRejected,
  updateQuoteDetails,
} from "../services/crmQuotesService";
import QuotePDFButton from "../components/QuotePDFButton";
import QuoteSendPanel from "../components/QuoteSendPanel";
import "../styles/crm.css";

const QuoteDetailsPage = () => {
  const { quoteId } = useParams();
  const { companyId } = useCompany();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [message, setMessage] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const { canConvertQuote } = useCrmPermissions();

  const load = useCallback(async () => {
    if (!companyId || !quoteId) return;

    setLoading(true);
    setMessage("");

    try {
      const result = await getQuoteById(companyId, quoteId);
      setData(result);
      setNotes(result.quote.notes || "");
      setTerms(result.quote.terms || "");
      setValidUntil(result.quote.valid_until || "");
    } catch (error) {
      setMessage(error.message || "Failed to load quote");
    } finally {
      setLoading(false);
    }
  }, [companyId, quoteId]);

  useEffect(() => {
    load();
  }, [load]);

  const defaultEmailSubject = useMemo(() => {
    if (!data?.quote) return "";
    return `Quotation ${data.quote.quote_number || ""} from KostPadi`;
  }, [data]);

  const defaultEmailBody = useMemo(() => {
    if (!data?.quote) return "";

    return [
      `Hello ${
        data.quote.crm_contacts?.full_name ||
        data.quote.crm_accounts?.name ||
        "Customer"
      },`,
      "",
      `Please find your quotation ${
        data.quote.quote_number || ""
      } attached/downloaded.`,
      `Quote Total: ₦${Number(data.quote.total || 0).toLocaleString()}`,
      `Valid Until: ${data.quote.valid_until || "N/A"}`,
      "",
      "Kindly review and let us know if you would like us to proceed.",
      "",
      "Best regards,",
      "KostPadi",
    ].join("\n");
  }, [data]);

  const handleSaveEdit = useCallback(async () => {
    if (!companyId || !quoteId) return;

    setBusyAction("save-edit");
    setMessage("");

    try {
      await updateQuoteDetails(companyId, quoteId, {
        notes: notes.trim() || null,
        terms: terms.trim() || null,
        valid_until: validUntil || null,
      });

      setEditMode(false);
      await load();
    } catch (error) {
      setMessage(error.message || "Failed to update quote");
    } finally {
      setBusyAction("");
    }
  }, [companyId, quoteId, notes, terms, validUntil, load]);

  const handleSend = useCallback(
    async ({ recipientEmail, emailSubject, emailBody }) => {
      if (!companyId || !quoteId || !data?.quote) return;

      setBusyAction("send");
      setMessage("");

      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) {
          throw userError;
        }

        if (!user?.id) {
          throw new Error("User not found");
        }

        await sendQuoteEmail({
          companyId,
          quoteId,
          quote: data.quote,
          items: data.items,
          to: recipientEmail,
          subject: emailSubject,
          body: emailBody,
          userId: user.id,
        });

        setSendOpen(false);
        setMessage("Quote email sent successfully.");
        await load();
      } catch (error) {
        try {
          await markQuoteEmailFailed({
            companyId,
            quoteId,
            to: recipientEmail,
            subject: emailSubject,
            body: emailBody,
            errorMessage: error.message || "Failed to send quote email",
          });
        } catch (secondaryError) {
          console.error(secondaryError.message);
        }

        setMessage(error.message || "Failed to send quote email");
      } finally {
        setBusyAction("");
      }
    },
    [companyId, quoteId, data, load]
  );

  const handleAccept = useCallback(async () => {
    if (!companyId || !quoteId) return;

    setBusyAction("accept");
    setMessage("");

    try {
      await markQuoteAccepted(companyId, quoteId);
      await load();
    } catch (error) {
      setMessage(error.message || "Failed to mark quote as accepted");
    } finally {
      setBusyAction("");
    }
  }, [companyId, quoteId, load]);

  const handleReject = useCallback(async () => {
    if (!companyId || !quoteId) return;

    setBusyAction("reject");
    setMessage("");

    try {
      await markQuoteRejected(companyId, quoteId);
      await load();
    } catch (error) {
      setMessage(error.message || "Failed to mark quote as rejected");
    } finally {
      setBusyAction("");
    }
  }, [companyId, quoteId, load]);

  const handleConvert = useCallback(async () => {
    if (!companyId || !quoteId) return;

    setBusyAction("convert");
    setMessage("");

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        throw userError;
      }

      if (!user.id || typeof user.id !== "string" || user.id.length < 10) {
        throw new Error("Invalid user ID for invoice creation.");
      }

      const result = await convertQuoteToInvoice(companyId, quoteId, user.id);
      setMessage(
        `Quote converted successfully. Invoice ID: ${result.invoice.id}`
      );
      await load();
    } catch (error) {
      setMessage(error.message || "Failed to convert quote");
    } finally {
      setBusyAction("");
    }
  }, [companyId, quoteId, load]);

  if (loading) {
    return <div className="crm-message">Loading quote...</div>;
  }

  if (!data) {
    return <div className="crm-message">{message || "No quote found."}</div>;
  }

  return (
    <div>
      <h2 className="crm-page-title">Quote Details</h2>
      <div className="crm-list-subtext">
        Email Status: {data.quote.email_status || "not_sent"}
      </div>
      <div className="crm-list-subtext">
        Email Error: {data.quote.email_error || "N/A"}
      </div>

      {message ? <div className="crm-message">{message}</div> : null}

      <div className="crm-table-card" style={{ marginBottom: "20px" }}>
        <h3 className="crm-list-title">
          {data.quote.quote_number || data.quote.id}
        </h3>
        <div className="crm-list-subtext">
          Status: {data.quote.status || "draft"}
        </div>
        <div className="crm-list-subtext">
          Account: {data.quote.crm_accounts?.name || "N/A"}
        </div>
        <div className="crm-list-subtext">
          Contact: {data.quote.crm_contacts?.full_name || "N/A"}
        </div>
        <div className="crm-list-subtext">
          Opportunity: {data.quote.crm_opportunities?.name || "N/A"}
        </div>
        <div className="crm-list-subtext">
          Issue Date: {data.quote.issue_date || "N/A"}
        </div>
        <div className="crm-list-subtext">
          Recipient Email: {data.quote.recipient_email || "N/A"}
        </div>

        {editMode ? (
          <div style={{ marginTop: "16px" }}>
            <div className="crm-form-row">
              <div className="crm-form-group">
                <label>Valid Until</label>
                <input
                  className="crm-input"
                  type="date"
                  value={validUntil}
                  onChange={(event) => setValidUntil(event.target.value)}
                />
              </div>

              <div className="crm-form-group">
                <label>Terms</label>
                <input
                  className="crm-input"
                  value={terms}
                  onChange={(event) => setTerms(event.target.value)}
                  placeholder="Enter terms"
                />
              </div>
            </div>

            <div className="crm-form-group">
              <label>Notes</label>
              <textarea
                className="crm-textarea"
                rows={4}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Enter notes"
              />
            </div>

            <div className="crm-inline-actions">
              <button
                className="crm-primary-btn"
                onClick={handleSaveEdit}
                disabled={busyAction === "save-edit"}
              >
                {busyAction === "save-edit" ? "Saving..." : "Save Changes"}
              </button>

              <button
                className="crm-secondary-btn"
                onClick={() => setEditMode(false)}
                disabled={busyAction === "save-edit"}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="crm-list-subtext">
              Valid Until: {data.quote.valid_until || "N/A"}
            </div>
            <div className="crm-list-subtext">
              Notes: {data.quote.notes || "N/A"}
            </div>
            <div className="crm-list-subtext">
              Terms: {data.quote.terms || "N/A"}
            </div>

            <div className="crm-inline-actions">
              <button
                className="crm-small-btn alt"
                onClick={() => setEditMode(true)}
              >
                Edit Quote
              </button>

              <button
                className="crm-small-btn"
                onClick={() => setSendOpen(true)}
                disabled={busyAction === "send"}
              >
                Send Quote
              </button>

              <button
                className="crm-small-btn alt"
                onClick={handleAccept}
                disabled={busyAction === "accept"}
              >
                {busyAction === "accept" ? "Updating..." : "Mark Accepted"}
              </button>

              <button
                className="crm-small-btn alt"
                onClick={handleReject}
                disabled={busyAction === "reject"}
              >
                {busyAction === "reject" ? "Updating..." : "Mark Rejected"}
              </button>

              {data.quote.status !== "converted" && canConvertQuote ? (
                <button
                  className="crm-primary-btn"
                  onClick={handleConvert}
                  disabled={busyAction === "convert"}
                >
                  {busyAction === "convert"
                    ? "Converting..."
                    : "Convert to Invoice"}
                </button>
              ) : null}

              {!canConvertQuote ? (
                <div className="crm-list-subtext">
                  Only admins can convert quotes to invoices.
                </div>
              ) : null}

              <QuotePDFButton quote={data.quote} items={data.items} />
            </div>
          </>
        )}
      </div>

      <div className="crm-table-card">
        <h3 className="crm-page-title" style={{ fontSize: "20px" }}>
          Line Items
        </h3>

        {data.items.length === 0 ? (
          <div className="crm-message">No line items found.</div>
        ) : (
          data.items.map((item) => (
            <div key={item.id} className="crm-list-card">
              <h3 className="crm-list-title">{item.item_name}</h3>
              <div className="crm-list-subtext">
                Description: {item.description || "N/A"}
              </div>
              <div className="crm-list-subtext">
                Quantity: {Number(item.quantity || 0).toLocaleString()}
              </div>
              <div className="crm-list-subtext">
                Unit Price: ₦{Number(item.unit_price || 0).toLocaleString()}
              </div>
              <div className="crm-list-subtext">
                Discount %:{" "}
                {Number(item.discount_percent || 0).toLocaleString()}
              </div>
              <div className="crm-list-subtext">
                Tax %: {Number(item.tax_percent || 0).toLocaleString()}
              </div>
              <div className="crm-list-subtext">
                Line Total: ₦{Number(item.line_total || 0).toLocaleString()}
              </div>
            </div>
          ))
        )}

        <div className="crm-grid" style={{ marginTop: "16px" }}>
          <div className="crm-kpi-card">
            <p className="crm-kpi-title">Subtotal</p>
            <h3 className="crm-kpi-value">
              ₦{Number(data.quote.subtotal || 0).toLocaleString()}
            </h3>
          </div>
          <div className="crm-kpi-card">
            <p className="crm-kpi-title">Discount</p>
            <h3 className="crm-kpi-value">
              ₦{Number(data.quote.discount_total || 0).toLocaleString()}
            </h3>
          </div>
          <div className="crm-kpi-card">
            <p className="crm-kpi-title">Tax</p>
            <h3 className="crm-kpi-value">
              ₦{Number(data.quote.tax_total || 0).toLocaleString()}
            </h3>
          </div>
          <div className="crm-kpi-card">
            <p className="crm-kpi-title">Total</p>
            <h3 className="crm-kpi-value">
              ₦{Number(data.quote.total || 0).toLocaleString()}
            </h3>
          </div>
        </div>
      </div>

      <QuoteSendPanel
        open={sendOpen}
        defaultRecipient={
          data.quote.recipient_email ||
          data.quote.crm_contacts?.email ||
          data.quote.crm_accounts?.email ||
          ""
        }
        defaultSubject={data.quote.email_subject || defaultEmailSubject}
        defaultBody={data.quote.email_body || defaultEmailBody}
        onClose={() => setSendOpen(false)}
        onSend={handleSend}
        busy={busyAction === "send"}
      />
    </div>
  );
};

export default QuoteDetailsPage;
